import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Card, Col, DatePicker, Dropdown, Empty, Input, Modal, Row, Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from "antd";
import {
    DollarOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
    EyeOutlined, MoreOutlined, PlusOutlined, DownOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined,
    ExclamationCircleFilled, MergeCellsOutlined, SearchOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import dayjs from "dayjs";
import { generateDecaissementsPDF, generateDecaissementsExcel, generateBonAPayerPDF, generateBonAPayerExcel, setEtatCompagnie } from "../stats/sinistre/etatsSinistresFunctions";
import axiosInstance from "../../variables/api/axiosInstance";
import { displayNumbers } from "../../variables/numbersFunctions";
import { frenchDate } from "../../variables/functions";
import { validateDecaissement, rejectDecaissement, validateDecaissementsGroupe, rejectDecaissementsGroupe, getDecaissementsByGroupe, regrouperDecaissements, updateMotifReglement, updateMotifReglementGroupe, annulerReglementTechniqueGroupe } from "../../hooks/useHooks";
import { useReactToPrint } from "react-to-print";
import { rightsService } from "../../services/rightsService";

import { aggregateGroupe, groupDecaissements } from "./docs/groupeReglement";
import OrdreDeDepense from "./docs/OrdreDeDepense";
import FicheDemandeReglementSinistre from "./docs/FicheDemandeReglementSinistre";
import QuittanceIndemnite from "./docs/QuittanceIndemnite";
import FixEnginCategorieModal from "./components/FixEnginCategorieModal";
import AntidatageDateReglement, { messageRefusAntidatage } from "./components/AntidatageDateReglement";
import AssignCheque from "./components/AssignCheque";
import RemiseCheque from "./components/RemiseCheque";
import { LOGGED_USER } from "../../variables/constants";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const PRIMARY = "#242166";
const { confirm } = Modal;

const ETAT = {
    VALIDE: { color: "#16a34a", bg: "#dcfce7", label: "Validé" },
    EN_ATTENTE: { color: "#d97706", bg: "#fef3c7", label: "En attente" },
    EN_ATTENTE_D_ENCAISSEMENT: { color: "#2563eb", bg: "#dbeafe", label: "À encaisser" },
    ENCAISSE: { color: "#145A32", bg: "#d5f4e6", label: "Encaissé" },
    CHEQUE_ANNULE: { color: "#dc2626", bg: "#fee2e2", label: "Chèque annulé" },
    REJETE: { color: "#dc2626", bg: "#fee2e2", label: "Rejeté" },
    // État synthétique d'un groupe dont les lignes ont des états différents.
    GROUPE: { color: "#7c3aed", bg: "#ede9fe", label: "États mixtes" },
};
const etatTag = (e) => {
    const m = ETAT[e] || ETAT.EN_ATTENTE;
    return <Tag style={{ backgroundColor: m.bg, color: m.color, border: "none", borderRadius: 6, padding: "3px 10px", fontWeight: 600 }}>{m.label}</Tag>;
};

// Distinction visuelle des règlements groupés : le parent et ses lignes filles
// partagent un liseré violet à gauche + un fond teinté (même langage que le
// cockpit sinistre V2), les filles étant plus claires et indentées (↳).
const GROUPE_CSS = `
.vdv2-table .ant-table-tbody > tr.vdv2-group-row > td { background: #f6f2ff !important; }
.vdv2-table .ant-table-tbody > tr.vdv2-group-row:hover > td { background: #efe9fd !important; }
.vdv2-table .ant-table-tbody > tr.vdv2-group-row > td:first-child { border-left: 3px solid #7c3aed; }
.vdv2-table .ant-table-tbody > tr.vdv2-group-child-row > td { background: #fbfaff !important; }
.vdv2-table .ant-table-tbody > tr.vdv2-group-child-row:hover > td { background: #f4f1fc !important; }
.vdv2-table .ant-table-tbody > tr.vdv2-group-child-row > td:first-child { border-left: 3px solid #ddd6fe; }
`;

/**
 * Listing des règlements (décaissements) — v2. UI moderne, filtre de dates SERVEUR
 * (déjà supporté par /api/decaissement/sinistre) + filtre compagnie (Pool), actions
 * valider / rejeter / voir le sinistre.
 */
const ValidationDecaissementsV2 = () => {
    const navigate = useNavigate();
    const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    // Par défaut : le MOIS EN COURS (du 1er au dernier jour). L'utilisateur peut élargir/effacer.
    const [periode, setPeriode] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
    const [compagnies, setCompagnies] = useState([]);
    const [compagnie, setCompagnie] = useState(null);
    // Filtre branche (déjà scopé par visibilité côté serveur : une compagnie ne voit que TPV/MOTO…).
    const [branches, setBranches] = useState([]);
    const [branche, setBranche] = useState(null);
    // Statut de paiement : null = tous, "BAP" = validé en attente de chèque, "PAYE" = avec chèque
    const [statutPaiement, setStatutPaiement] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const [fixCategorieData, setFixCategorieData] = useState(null);

    // Recherche (texte immédiat + version débouncée pilotant le fetch). Déclarés AVANT l'effet de fetch
    // pour éviter la zone morte temporelle (TDZ) : le tableau de deps de l'effet les référence.
    const [searchText, setSearchText] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setSearchQuery((searchText || "").trim()), 350);
        return () => clearTimeout(t);
    }, [searchText]);

    // Grouped documents / printing states
    const [modalOrdreDepense, setModalOrdreDepense] = useState(false);
    // Saisie du motif du règlement depuis l'ordre de dépense (règlements multiples).
    const [odMotif, setOdMotif] = useState("");
    const [savingOdMotif, setSavingOdMotif] = useState(false);
    const canEditMotif = rightsService("HSI089");
    const canAnnulerTech = rightsService("HSI015");
    const [ordreDepense, setOrdreDepense] = useState(null);
    const [modalFicheDemandeReglement, setModalFicheDemandeReglement] = useState(false);
    const [ficheDemandeReglement, setFicheDemandeReglement] = useState(null);
    const [modalQuittance, setModalQuittance] = useState(false);
    const [quittance, setQuittance] = useState(null);
    // Dossier sinistre complet passé aux documents groupés (en-tête, SAP, assuré…).
    const [docSinistre, setDocSinistre] = useState(null);
    // Assignation d'un chèque unique à tout le lot (lignes VALIDE du groupe).
    const [modalAssignCheque, setModalAssignCheque] = useState(false);
    const [assignChequeGroupe, setAssignChequeGroupe] = useState(null);
    // Remise de chèque (encaissement) d'un règlement groupé — comme pour un règlement simple.
    const [modalRemiseCheque, setModalRemiseCheque] = useState(false);
    const [remiseDecaissement, setRemiseDecaissement] = useState(null);

    const printODRef = useRef(null);
    const printFDRef = useRef(null);
    const printQRef = useRef(null);

    const exportODToPdf = useReactToPrint({ content: () => printODRef.current });
    const exportFDToPdf = useReactToPrint({ content: () => printFDRef.current });
    const exportQToPdf = useReactToPrint({ content: () => printQRef.current });

    useEffect(() => {
        if (isPool) {
            // Assurances + le POOL : permet de filtrer les décaissements de sinistres gérés par
            // le Pool (GUM/VT…) en sélectionnant le Pool dans la liste « Compagnie ».
            axiosInstance.get(`/api/organisme/all/assurances-pool`).then((r) => setCompagnies(r.data || [])).catch(() => { });
        }
        // Branches visibles pour l'utilisateur (le serveur masque VT/GUM aux compagnies).
        axiosInstance.get(`/api/branche`).then((r) => setBranches(Array.isArray(r.data) ? r.data : [])).catch(() => setBranches([]));
    }, [isPool]);

    useEffect(() => {
        setLoading(true);
        // Recherche active → on charge une grande page (tout le mois/filtre) pour que le filtre client
        // couvre l'INTÉGRALITÉ des résultats, pas seulement la page courante (la source ne filtre pas
        // sur le bénéficiaire). Sinon, pagination serveur normale.
        const searching = !!searchQuery;
        const params = { page: searching ? 0 : page - 1, size: searching ? 5000 : pageSize };
        if (periode?.length === 2) {
            params.startDate = periode[0].format("YYYY-MM-DD");
            params.endDate = periode[1].format("YYYY-MM-DD");
        }
        if (compagnie) params.compagnie = compagnie;
        if (branche) params.branche = branche;
        if (statutPaiement) params.statutPaiement = statutPaiement;
        axiosInstance.get(`/api/decaissement/sinistre`, { params })
            .then((res) => {
                const { content = [], totalElements = 0 } = res.data || {};
                setRows(content);
                setTotal(totalElements);
            })
            .catch((e) => console.log(e))
            .finally(() => setLoading(false));
    }, [page, pageSize, periode, compagnie, branche, statutPaiement, reloadTick, searchQuery]);

    const refresh = () => setReloadTick((t) => t + 1);

    // ── Export Excel / PDF — CONTRAINT à la sélection de l'état de paiement ──────────
    // BAP  → « ÉTAT DES SINISTRES BON À PAYER » (même source/format que /etats/sinistres).
    // PAYE → état actuel (« états des sinistres payés »).
    // On exporte TOUT le périmètre filtré (période + compagnie), pas seulement la page courante.
    const [exporting, setExporting] = useState(false);
    const downloadPdf = (url, filename) => {
        if (!url) return;
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };
    const handleExport = async (type) => {
        if (!statutPaiement) {
            toast.info("Sélectionnez d'abord un état de paiement (BAP ou Payé) pour exporter.");
            return;
        }
        setExporting(true);
        try {
            const dateArg = periode?.length === 2
                ? [periode[0].format("YYYY-MM-DD"), periode[1].format("YYYY-MM-DD")]
                : [null, null];
            // « Catégorie » du document = la BRANCHE sélectionnée (et NON l'organisme, qui s'affichait
            // à tort). La compagnie va dans le contexte d'entête → « · Organisme : … ».
            const catLabel = branche
                ? (branches.find((b) => b.codeBranche === branche)?.nom || branche)
                : "Toutes catégories";
            setEtatCompagnie(compagnie
                ? (compagnies.find((c) => c.code === compagnie)?.raisonSociale || compagnie)
                : null);

            if (statutPaiement === "BAP") {
                // Source dédiée « Bon à payer » (inclut le motif de règlement).
                const res = await axiosInstance.get(`/api/decaissement/sinistres-bons-a-payer`, {
                    params: {
                        startDate: dateArg[0], endDate: dateArg[1],
                        ...(compagnie ? { compagnie } : {}),
                        ...(branche ? { branche } : {}),
                    },
                });
                const all = res.data || [];
                if (!all.length) {
                    toast.info("Aucun sinistre bon à payer à exporter pour ces filtres.");
                    return;
                }
                if (type === "excel") {
                    generateBonAPayerExcel(all, catLabel, dateArg);
                } else {
                    const url = await generateBonAPayerPDF(all, catLabel, dateArg);
                    downloadPdf(url, `etat-bon-a-payer-${dateArg[0] || ""}-${dateArg[1] || ""}.pdf`);
                }
            } else {
                // PAYE : état actuel des règlements payés.
                const params = { page: 0, size: 100000, statutPaiement: "PAYE" };
                if (dateArg[0]) { params.startDate = dateArg[0]; params.endDate = dateArg[1]; }
                if (compagnie) params.compagnie = compagnie;
                if (branche) params.branche = branche;
                const res = await axiosInstance.get(`/api/decaissement/sinistre`, { params });
                const all = res.data?.content || [];
                if (!all.length) {
                    toast.info("Aucun règlement payé à exporter pour ces filtres.");
                    return;
                }
                if (type === "excel") {
                    await generateDecaissementsExcel(all, catLabel, dateArg);
                } else {
                    const url = await generateDecaissementsPDF(all, catLabel, dateArg);
                    downloadPdf(url, `etat-reglements-payes-${dateArg[0] || ""}-${dateArg[1] || ""}.pdf`);
                }
            }
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de l'export.");
        } finally {
            setExporting(false);
        }
    };

    const handleValider = (record) => {
        const dateAntidatage = { current: null };
        confirm({
            title: "Valider ce décaissement ?",
            icon: <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 24 }} />,
            content: (
                <>
                    <Card size="small" style={{ backgroundColor: "#f9fbf9", marginTop: 8 }}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <div><strong>Sinistre :</strong> {record.numeroSinistreM || record.numeroSinistreP}</div>
                            <div><strong>Bénéficiaire :</strong> {record.beneficiaire}</div>
                            <div><strong>Montant :</strong> {displayNumbers(record.montantPaye)} F CFA</div>
                        </Space>
                    </Card>
                    <AntidatageDateReglement dateRef={dateAntidatage} />
                </>
            ),
            okText: "Valider", cancelText: "Annuler",
            okButtonProps: { style: { backgroundColor: "#16a34a", borderColor: "#16a34a" } },
            async onOk() {
                try {
                    await validateDecaissement(record.codeId, dateAntidatage.current);
                    toast.success("Décaissement validé avec succès");
                    refresh();
                } catch (e) {
                    const data = e?.response?.data;
                    if (e?.response?.status === 422 && data?.code === "ENGIN_CATEGORIE_MANQUANTE") {
                        if (data.enginCodeId) {
                            setFixCategorieData({ enginCodeId: data.enginCodeId, immatriculation: data.immatriculation, decaissementCodeId: record.codeId, dateReglement: dateAntidatage.current });
                        } else {
                            toast.warning(data.message || "Aucun véhicule rattaché au sinistre. Rattachez-le avant de régler.");
                        }
                    } else if (messageRefusAntidatage(e)) {
                        toast.error(messageRefusAntidatage(e));
                    } else {
                        console.error(e);
                        toast.error("Erreur lors de la validation");
                    }
                }
            },
        });
    };

    const handleRejeter = (record) => {
        confirm({
            title: "Rejeter ce décaissement ?",
            icon: <CloseCircleOutlined style={{ color: "#dc2626", fontSize: 24 }} />,
            okText: "Rejeter", okType: "danger", cancelText: "Annuler",
            async onOk() {
                try {
                    await rejectDecaissement(record.codeId);
                    toast.success("Décaissement rejeté avec succès");
                    refresh();
                } catch (e) { console.error(e); toast.error("Erreur lors du rejet"); }
            },
        });
    };

    const voirSinistre = (record) => {
        if (record.codeId || record.sinistreCodeId) {
            navigate("/details-sinistre/v2", { state: { codeId: record.sinistreCodeId || record.codeId } });
        }
    };

    // Règlement multiple : règlements cumulés par expert, entité de constat,
    // conseil juridique ou établissement (pharmacies, cliniques…).
    const reglementMultipleItems = [
        { key: "experts", label: "Les experts" },
        { key: "entites", label: "Les entités de constat" },
        { key: "conseils", label: "Les conseils juridiques" },
        { key: "etablissements", label: "Les établissements (pharmacies, cliniques…)" },
    ];
    const onReglementMultipleClick = (key) => {
        if (key === "experts") navigate("/paiements-sinistre-expert/v2");
        else if (key === "entites") navigate("/paiements-sinistre-entites/v2");
        else if (key === "conseils") navigate("/paiements-sinistre-conseil-juridique/v2");
        else if (key === "etablissements") navigate("/paiements-sinistre-etablissement/v2");
    };

    const menuItems = (record) => {
        const items = [{ key: "voir", icon: <EyeOutlined />, label: "Voir le sinistre" }];
        if (record.etatDecaissement === "EN_ATTENTE") {
            items.push({ key: "valider", icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />, label: "Valider" });
            items.push({ key: "rejeter", icon: <CloseCircleOutlined style={{ color: "#dc2626" }} />, label: "Rejeter" });
        }
        return items;
    };
    const onMenuClick = (key, record) => {
        if (key === "voir") voirSinistre(record);
        else if (key === "valider") handleValider(record);
        else if (key === "rejeter") handleRejeter(record);
    };

    const columns = [
        {
            title: "N° Sinistre", key: "numSinistre", render: (_, r) => {
                if (r.isGroupParent) {
                    return (
                        <Tag color="purple" style={{ margin: 0, fontWeight: 600 }}>
                            Groupé · {r._lignes?.length} ligne(s)
                        </Tag>
                    );
                }
                return (
                    <Text strong style={{ fontFamily: "monospace" }}>
                        {r.isGroupChild && <span style={{ color: "#a78bfa", marginRight: 6 }}>↳</span>}
                        {r.numeroSinistreM || r.numeroSinistreP}
                    </Text>
                );
            },
        },
        { title: "N° Police", key: "numPolice", render: (_, r) => <Text style={{ color: "#666", fontFamily: "monospace" }}>{r.numeroPoliceM || r.numeroPoliceP}</Text> },
        { title: "Souscripteur", dataIndex: "souscripteur", key: "souscripteur" },
        {
            title: "Bénéficiaire", key: "beneficiaire", render: (_, r) => {
                // Ligne fille d'un groupe : le bénéficiaire est celui du groupe, on
                // l'affiche en discret pour ne pas la confondre avec un règlement isolé.
                if (r.isGroupChild) {
                    return (
                        <div style={{ paddingLeft: 8 }}>
                            <div style={{ fontWeight: 500, color: "#6b7280" }}>{r.beneficiaire || "—"}</div>
                            <div style={{ fontSize: 11, color: "#a78bfa" }}>Ligne du règlement groupé</div>
                        </div>
                    );
                }
                const isGroup = !!r.isGroupParent;
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: isGroup ? 9 : "50%", flexShrink: 0,
                            backgroundColor: isGroup ? "#7c3aed" : "#4f46e5", color: "#fff", fontSize: 14, fontWeight: 600,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            {isGroup ? "G" : (r.beneficiaire?.charAt(0)?.toUpperCase() || "B")}
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, color: "#1a1a1a" }}>{r.beneficiaire || "—"}</div>
                            {isGroup && (
                                <div style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600 }}>
                                    Règlement groupé — n° d'ordre unique
                                </div>
                            )}
                            {r.numeroCheque && (
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    Chèque N° {r.numeroCheque}{r.banque ? ` - ${r.banque}` : ""}
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Montant", dataIndex: "montantPaye", key: "montant", align: "right", render: (v, r) => (
                <span>
                    {r.isGroupParent && <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Total</Text>}
                    <Text strong style={{ color: r.isGroupParent ? "#7c3aed" : "#059669" }}>{displayNumbers(v || 0)} F</Text>
                </span>
            ),
        },
        { title: "État", dataIndex: "etatDecaissement", key: "etat", align: "center", render: (e) => etatTag(e) },
        {
            // Date effective = date de règlement si réglé, sinon date de saisie (created_at).
            // C'est exactement la date sur laquelle porte le filtre de période → cohérence.
            title: "Date de règlement", key: "date",
            render: (_, r) => {
                const regle = r.dateReglement;
                const eff = regle || r.dateSaisie;
                return (
                    <span>
                        <ClockCircleOutlined style={{ marginRight: 6, color: "#4f46e5" }} />
                        {eff ? frenchDate(eff) : "—"}
                        {!regle && eff && (
                            <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>(saisi)</Text>
                        )}
                    </span>
                );
            },
        },
        {
            title: "", key: "action", align: "center", width: 50, render: (_, r) => {
                const isGroup = !!r?.isGroupParent;
                const items = isGroup ? getGroupMenuItems(r) : menuItems(r);
                const onClick = isGroup ? (e) => handleGroupMenuClick(e, r) : (e) => onMenuClick(e.key, r);
                return (
                    <Dropdown trigger={["click"]} menu={{ items, onClick }}>
                        <Button type="text" icon={<MoreOutlined />} style={{ fontSize: 18 }} />
                    </Dropdown>
                );
            },
        },
    ];

    // --- Grouping : source de données arborescente similaire à ReglementsTab
    const groupedDataSource = useMemo(() => {
        const sum = (lignes, field) => (lignes || []).reduce((acc, d) => acc + (parseFloat(d?.[field]) || 0), 0);
        const uniq = (vals) => Array.from(new Set(vals.filter(Boolean)));
        return groupDecaissements(rows).map(({ key, lignes }) => {
            if (lignes.length === 1 && !lignes[0]?.groupeReglementCodeId) return lignes[0];
            const first = lignes[0];
            const etatCommun = lignes.every((d) => d?.etatDecaissement === first?.etatDecaissement) ? first?.etatDecaissement : "GROUPE";
            // Souscripteur / police affichés sur le parent seulement s'ils sont
            // communs à toutes les lignes (un groupe peut couvrir plusieurs dossiers).
            const souscripteurs = uniq(lignes.map((l) => l?.souscripteur));
            const polices = uniq(lignes.map((l) => l?.numeroPoliceM || l?.numeroPoliceP));
            return {
                codeId: key,
                isGroupParent: true,
                groupeReglementCodeId: first?.groupeReglementCodeId,
                beneficiaire: first?.beneficiaire,
                numero: first?.numero,
                numeroCheque: first?.numeroCheque,
                banque: first?.banque,
                souscripteur: souscripteurs.length === 1 ? souscripteurs[0] : (souscripteurs.length > 1 ? `${souscripteurs.length} dossiers` : undefined),
                numeroPoliceM: polices.length === 1 ? polices[0] : undefined,
                // Total du groupe (= montant NET à payer au bénéficiaire) : Σ montantPaye = TTC − retenue
                // (la retenue est prélevée, pas versée). EXCLUT les lignes REJETÉES. Pour un règlement
                // normal, montantGlobal = montantPaye (aucun changement) ; pour un conseil juridique /
                // expert, montantPaye = montant global − retenue.
                montantPaye: sum(lignes.filter((l) => l?.etatDecaissement !== "REJETE"), "montantPaye"),
                dateReglement: first?.dateReglement,
                etatDecaissement: etatCommun,
                _lignes: lignes,
                children: lignes.map((l) => ({ ...l, isGroupChild: true })),
            };
        });
    }, [rows]);

    // --- Recherche (bénéficiaire en priorité, + assuré / police / n° ordre / n° chèque). Le fetch charge
    // tout le mois/filtre quand une recherche est active (voir searchQuery) → le filtre ci-dessous couvre
    // l'INTÉGRALITÉ des résultats. Un groupe reste visible si son parent OU l'une de ses filles correspond.
    const filteredDataSource = useMemo(() => {
        const q = (searchText || "").trim().toLowerCase();
        if (!q) return groupedDataSource;
        const norm = (v) => (v == null ? "" : v.toString().toLowerCase());
        const hit = (r) => norm(r?.beneficiaire).includes(q) || norm(r?.souscripteur).includes(q)
            || norm(r?.numeroPoliceM).includes(q) || norm(r?.numeroPoliceP).includes(q)
            || norm(r?.numero).includes(q) || norm(r?.numeroCheque).includes(q);
        return groupedDataSource.filter((r) => hit(r) || (r?.children || []).some(hit));
    }, [groupedDataSource, searchText]);

    // --- Regroupement MANUEL de règlements EN_ATTENTE déjà créés (correction d'un groupement mal fait
    // ou antérieur à la mise en place du regroupement). Éligibles : lignes isolées (non déjà groupées),
    // EN_ATTENTE, sans chèque. Un groupe = un bénéficiaire (verrouillé à la 1re sélection).
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [selectedRowsAll, setSelectedRowsAll] = useState([]);
    const [regrouping, setRegrouping] = useState(false);

    // Éligibles au (re)groupement : règlements simples EN_ATTENTE sans chèque, ET groupes ENTIÈREMENT
    // EN_ATTENTE (etatDecaissement === "EN_ATTENTE" ⇒ toutes les lignes le sont) sans chèque. On peut
    // ainsi AJOUTER des simples à un groupe et FUSIONNER deux groupes. On ne sélectionne pas les enfants
    // (on coche le parent). Un groupe déjà validé / avec chèque n'est pas éligible.
    const isRegroupable = (r) => {
        if (!r || r.isGroupChild) return false;
        if (r.isGroupParent) {
            // Robuste : on lit directement les lignes du groupe (toutes EN_ATTENTE, sans chèque),
            // indépendamment de l'état agrégé affiché.
            const ls = r._lignes || [];
            return ls.length > 0 && ls.every((l) => l?.etatDecaissement === "EN_ATTENTE" && !l?.numeroCheque);
        }
        return !r.groupeReglementCodeId && r.etatDecaissement === "EN_ATTENTE" && !r.numeroCheque;
    };
    // Déplie une sélection (parents → leurs lignes) en codeId de décaissements réels.
    const expandRegroupables = (rowsSel) => {
        const ids = [];
        (rowsSel || []).forEach((r) => {
            if (r?.isGroupParent) (r._lignes || []).forEach((l) => { if (l?.codeId) ids.push(l.codeId); });
            else if (r?.codeId) ids.push(r.codeId);
        });
        return Array.from(new Set(ids));
    };

    const regroupables = useMemo(() => selectedRowsAll.filter(isRegroupable), [selectedRowsAll]);
    const benefSelection = useMemo(
        () => Array.from(new Set(regroupables.map((r) => (r.beneficiaire || "").trim().toUpperCase()))),
        [regroupables]);

    const rowSelection = {
        selectedRowKeys: selectedKeys,
        checkStrictly: true,
        preserveSelectedRowKeys: true,
        columnWidth: 44,
        onChange: (keys, rows) => { setSelectedKeys(keys); setSelectedRowsAll((rows || []).filter(Boolean)); },
        getCheckboxProps: (r) => {
            const eligible = isRegroupable(r);
            // Une fois un bénéficiaire choisi, on verrouille la sélection sur celui-ci (1 groupe = 1 payé).
            const autreBenef = regroupables.length > 0
                && (r?.beneficiaire || "").trim().toUpperCase() !== benefSelection[0];
            return { disabled: !eligible || autreBenef };
        },
    };

    const handleRegrouper = () => {
        if (regroupables.length < 2) return;
        if (benefSelection.length > 1) {
            toast.error("Sélectionnez des règlements d'un même bénéficiaire.");
            return;
        }
        const ids = expandRegroupables(regroupables);
        if (ids.length < 2) return;
        const total = regroupables.reduce((a, r) => a + (parseFloat(r.montantPaye) || 0), 0);
        const nbGroupes = regroupables.filter((r) => r.isGroupParent).length;
        const fusion = nbGroupes >= 1; // ajout à un groupe existant ou fusion de groupes
        confirm({
            title: fusion ? "Regrouper / fusionner la sélection ?" : "Regrouper les règlements sélectionnés ?",
            icon: <ExclamationCircleFilled style={{ color: "#7c3aed" }} />,
            content: (
                <div>
                    <b>{ids.length}</b> règlement(s) de <b>{regroupables[0]?.beneficiaire || "—"}</b> seront
                    réunis en un seul règlement groupé (total <b>{displayNumbers(total)} F CFA</b>), validable et
                    payable en une seule fois.
                    {fusion && (
                        <div style={{ marginTop: 6, color: "#6b7280" }}>
                            {nbGroupes >= 2 ? "Fusion de plusieurs groupes en attente." : "Ajout au groupe existant sélectionné."}
                        </div>
                    )}
                </div>
            ),
            okText: fusion ? "Regrouper" : "Regrouper",
            okButtonProps: { style: { background: "#7c3aed", border: "none" } },
            cancelText: "Annuler",
            onOk: async () => {
                setRegrouping(true);
                try {
                    await regrouperDecaissements(ids);
                    toast.success("Règlements regroupés avec succès.");
                    setSelectedKeys([]); setSelectedRowsAll([]);
                    refresh();
                } catch (e) {
                    toast.error(e?.response?.data?.message || e?.response?.data?.error
                        || "Erreur lors du regroupement des règlements.");
                } finally { setRegrouping(false); }
            },
        });
    };

    // Les lignes du tableau sont des DTO plats (bénéficiaire en texte, pas de
    // nature ni de montantGlobal, pas d'objet sinistre) : avant d'imprimer un
    // document groupé, on recharge les décaissements COMPLETS du groupe puis le
    // dossier sinistre (SAP, évaluation, assuré…) comme le fait le détail sinistre.
    const loadGroupeForDocs = async (record) => {
        try {
            const res = await getDecaissementsByGroupe(record.groupeReglementCodeId);
            // L'endpoint renvoie { decaissement, sinistreCodeId, numeroSinistre, dateSinistre,
            // numeroPolice, immatriculation, assure } car Decaissement.sinistre n'est jamais
            // sérialisé (@JsonBackReference) : on reconstruit la forme attendue par aggregateGroupe.
            const lignes = (res.data || []).map((x) => ({
                ...x.decaissement,
                sinistre: {
                    codeId: x.sinistreCodeId,
                    numeroSinistreM: x.numeroSinistre,
                    dateSinistre: x.dateSinistre,
                    contrat: { numeroPoliceM: x.numeroPolice, souscripteur: x.assure ? { nom: x.assure } : null },
                    engin: { immatriculation: x.immatriculation },
                },
            }));
            if (!lignes.length) {
                toast.error("Aucune ligne trouvée pour ce règlement groupé.");
                return null;
            }
            const agg = aggregateGroupe(lignes);
            let sin = lignes[0]?.sinistre || null;
            if (sin?.codeId) {
                try {
                    const rs = await axiosInstance.get(`/api/sinistre/codeId/${sin.codeId}`);
                    sin = rs.data || sin;
                } catch (err) {
                    console.error(err); // on garde le sinistre minimal reconstruit
                }
            }
            return { agg, sinistre: sin };
        } catch (err) {
            console.error(err);
            toast.error("Impossible de charger les détails du règlement groupé.");
            return null;
        }
    };

    // Enregistre le motif saisi depuis l'ordre de dépense. Règlement multiple → applique à tout le
    // groupe (groupeReglementCodeId) ; sinon au seul décaissement. HSI089 côté back.
    const enregistrerMotifOD = async () => {
        const grp = ordreDepense?.groupeReglementCodeId;
        const estMultiple = !!(ordreDepense?.isReglementMultiple && grp);
        try {
            setSavingOdMotif(true);
            if (estMultiple) {
                await updateMotifReglementGroupe(grp, odMotif);
            } else if (ordreDepense?.codeId) {
                await updateMotifReglement(ordreDepense.codeId, odMotif);
            } else {
                toast.warn("Règlement introuvable pour l'enregistrement du motif.");
                return;
            }
            setOrdreDepense((prev) => ({ ...prev, motifReglement: odMotif }));
            toast.success("Motif du règlement enregistré.");
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.message || e?.response?.data?.error
                || "Erreur lors de l'enregistrement du motif.");
        } finally {
            setSavingOdMotif(false);
        }
    };

    // Opération LONGUE (validation groupée multi-dossiers) : le proxy peut renvoyer 502/504 alors que
    // la transaction backend a ABOUTI. On distingue ce cas d'un vrai échec applicatif (4xx avec corps).
    const estTimeoutPasserelle = (err) => {
        const s = err?.response?.status;
        return !err?.response || s === 502 || s === 503 || s === 504 || err?.code === "ECONNABORTED";
    };
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    // Vérifie l'état réel du groupe : la validation est appliquée s'il ne reste AUCUNE ligne EN_ATTENTE.
    const groupeSansEnAttente = async (groupeCodeId) => {
        const res = await getDecaissementsByGroupe(groupeCodeId);
        const lignes = res?.data || [];
        if (!lignes.length) return false;
        return !lignes.some((x) => (x?.decaissement?.etatDecaissement ?? x?.etatDecaissement) === "EN_ATTENTE");
    };
    // Attend (polling) que la validation groupée se reflète en base après un timeout passerelle.
    const attendreFinValidationGroupe = async (groupeCodeId, { tries = 8, delayMs = 3000 } = {}) => {
        for (let k = 0; k < tries; k++) {
            await sleep(delayMs);
            try { if (await groupeSansEnAttente(groupeCodeId)) return true; } catch (_) { /* on retente */ }
        }
        return false;
    };

    const handleGroupMenuClick = async (e, record) => {
        switch (e.key) {
            case "validateGroup": {
                const dateAntidatage = { current: null };
                confirm({
                    title: "Valider tous les décaissements de ce groupe ?",
                    content: <AntidatageDateReglement dateRef={dateAntidatage} />,
                    onOk: async () => {
                        try {
                            await validateDecaissementsGroupe(record.groupeReglementCodeId, dateAntidatage.current);
                            toast.success("Groupe validé avec succès");
                            refresh();
                        } catch (err) {
                            console.error(err);
                            // Timeout passerelle (502/504) sur une validation longue : la transaction a
                            // probablement abouti. On vérifie l'état réel avant d'annoncer une erreur.
                            if (estTimeoutPasserelle(err)) {
                                toast.info("Validation longue en cours — vérification de l'état du groupe…");
                                const ok = await attendreFinValidationGroupe(record.groupeReglementCodeId);
                                if (ok) toast.success("Groupe validé avec succès");
                                else toast.warn("La validation est peut-être encore en cours. Actualisez dans un instant.");
                                refresh();
                                return;
                            }
                            const data = err?.response?.data;
                            // Dépassement du plafond SAP d'un sinistre du groupe : au lieu d'un simple
                            // message, on propose d'aller DIRECTEMENT réévaluer le sinistre (ou voir son détail).
                            if (err?.response?.status === 422 && data?.code === "SAP_DEPASSE") {
                                const scid = data?.sinistreCodeId || record?.sinistreCodeId || record?.codeId;
                                confirm({
                                    title: "Plafond SAP dépassé",
                                    content: (
                                        <div>
                                            <p>{data.message || "Le montant à régler dépasse le reste disponible du sinistre."}</p>
                                            <p style={{ marginBottom: 0 }}>Réévaluez le sinistre pour augmenter la provision, ou ouvrez son détail.</p>
                                        </div>
                                    ),
                                    okText: "Réévaluer le sinistre",
                                    cancelText: "Voir le sinistre",
                                    onOk: () => navigate("/details-sinistre/v2", { state: { codeId: scid, openReeval: true } }),
                                    onCancel: () => navigate("/details-sinistre/v2", { state: { codeId: scid } }),
                                });
                            } else {
                                toast.error(messageRefusAntidatage(err) || "Erreur lors de la validation du groupe");
                            }
                        }
                    }
                });
                break;
            }
            case "rejectGroup": {
                confirm({
                    title: "Rejeter tous les décaissements de ce groupe ?",
                    icon: <ExclamationCircleFilled />,
                    content: "Toutes les lignes EN ATTENTE de ce règlement groupé passeront en REJETÉ.",
                    okText: "Rejeter", okType: "danger", cancelText: "Annuler",
                    onOk: async () => {
                        try {
                            await rejectDecaissementsGroupe(record.groupeReglementCodeId);
                            toast.success("Groupe rejeté avec succès");
                            refresh();
                        } catch (err) {
                            console.error(err);
                            toast.error("Erreur lors du rejet du groupe");
                        }
                    }
                });
                break;
            }
            case "annulerTechGroup": {
                confirm({
                    title: "Annuler techniquement ce règlement groupé ?",
                    icon: <ExclamationCircleFilled />,
                    content: (
                        <div>
                            <p style={{ marginBottom: 6 }}>
                                Chaque ligne du groupe sera <b>annulée</b> : contre-passation comptable des lignes
                                validées, ligne négative de trace, et passage en <b>REJETÉ</b> (exclu des cumuls / SAP).
                            </p>
                            <p style={{ marginBottom: 0, color: "#a8071a" }}>
                                Si un chèque est encore actif, annulez-le d'abord. Opération irréversible.
                            </p>
                        </div>
                    ),
                    okText: "Annuler le groupe", okType: "danger", cancelText: "Fermer",
                    onOk: async () => {
                        try {
                            await annulerReglementTechniqueGroupe(record.groupeReglementCodeId);
                            toast.success("Règlement groupé annulé techniquement");
                            refresh();
                        } catch (err) {
                            console.error(err);
                            toast.error(err?.response?.data?.message || "Erreur lors de l'annulation technique du groupe");
                        }
                    }
                });
                break;
            }
            case "assignChequeGroup": {
                // On n'assigne le chèque qu'aux lignes VALIDÉES (non encore payées) du groupe — les
                // lignes déjà payées ou encore en attente de validation ne sont pas concernées.
                const aAssigner = (record._lignes || []).filter((l) => l?.etatDecaissement === "VALIDE");
                if (!aAssigner.length) { toast.warn("Aucune ligne validée à payer dans ce groupe."); break; }
                // Lignes DTO plates : AssignCheque affiche montantGlobal dans son bandeau récap → repli sur montantPaye.
                setAssignChequeGroupe(aAssigner.map((l) => ({ ...l, montantGlobal: l.montantGlobal ?? l.montantPaye })));
                setModalAssignCheque(true);
                break;
            }
            case "remiseGroup": {
                // Remise (encaissement) du chèque du lot : on prend une ligne du groupe qui porte le
                // chèque assigné (EN_ATTENTE_D_ENCAISSEMENT) — le backend encaisse tout le lot du chèque.
                const ligne = (record._lignes || []).find((l) => l?.etatDecaissement === "EN_ATTENTE_D_ENCAISSEMENT" && l?.numeroCheque)
                    || (record._lignes || []).find((l) => l?.numeroCheque)
                    || (record._lignes || [])[0];
                if (!ligne) { toast.warn("Aucune ligne à encaisser dans ce groupe."); break; }
                setRemiseDecaissement(ligne);
                setModalRemiseCheque(true);
                break;
            }
            case "ordreDepenseGroup": {
                const data = await loadGroupeForDocs(record);
                if (!data) break;
                setDocSinistre(data.sinistre);
                setOrdreDepense(data.agg);
                // Aperçu INTERACTIF (au lieu d'imprimer directement) : permet de saisir le motif du
                // règlement multiple avant impression.
                setOdMotif(data.agg?.motifReglement || "");
                setModalOrdreDepense(true);
                break;
            }
            case "ficheGroup": {
                const data = await loadGroupeForDocs(record);
                if (!data) break;
                setDocSinistre(data.sinistre);
                setFicheDemandeReglement(data.agg);
                setModalFicheDemandeReglement(true);
                setTimeout(() => { exportFDToPdf(); setModalFicheDemandeReglement(false); }, 250);
                break;
            }
            case "quittanceGroup": {
                const data = await loadGroupeForDocs(record);
                if (!data) break;
                setDocSinistre(data.sinistre);
                setQuittance(data.agg);
                setModalQuittance(true);
                setTimeout(() => { exportQToPdf(); setModalQuittance(false); }, 250);
                break;
            }
            default:
                break;
        }
    };

    // Menu d'un règlement GROUPÉ : basé sur les ÉTATS DES LIGNES (et non sur l'état agrégé, qui vaut
    // « GROUPE » dès que les lignes diffèrent — ce qui masquait à tort Valider / Assigner un chèque).
    const getGroupMenuItems = (record) => {
        const lignes = record?._lignes || [];
        const some = (etat) => lignes.some((l) => l?.etatDecaissement === etat);
        const hasEnAttente = some("EN_ATTENTE");
        const hasValide = some("VALIDE");
        const hasAttEncaissement = some("EN_ATTENTE_D_ENCAISSEMENT");
        const hasEncaisse = some("ENCAISSE");
        const items = [];
        if (hasEnAttente) {
            items.push({ key: "validateGroup", icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />, label: "Valider le groupe" });
            items.push({ key: "rejectGroup", icon: <CloseCircleOutlined style={{ color: "#dc2626" }} />, label: "Rejeter le groupe" });
        }
        // Un seul chèque pour tout le lot : dès qu'au moins une ligne est VALIDÉE (et non encore payée).
        if (hasValide) {
            items.push({ key: "assignChequeGroup", icon: <DollarOutlined style={{ color: "#16a34a" }} />, label: "Assigner un chèque (lot)" });
        }
        // Documents (ordre de dépense / fiche) toujours consultables sur un groupe.
        items.push({ key: "ordreDepenseGroup", icon: <FilePdfOutlined />, label: "Ordre de dépense groupé" });
        items.push({ key: "ficheGroup", icon: <FilePdfOutlined />, label: "Fiche demande groupée" });
        // Chèque assigné → quittance + remise de chèque (encaissement), comme pour un règlement simple.
        if (hasAttEncaissement || hasEncaisse) {
            items.push({ key: "quittanceGroup", icon: <FileTextOutlined />, label: "Quittance groupée" });
        }
        if (hasAttEncaissement) {
            items.push({ key: "remiseGroup", icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />, label: "Remise de chèque (lot)" });
        }
        // Annulation technique du groupe (HSI015) : disponible dès qu'une ligne est engagée (validée /
        // chèque assigné / encaissé / chèque annulé). Si un chèque est encore actif, le backend exige
        // de l'annuler d'abord (message dédié).
        const hasEngage = hasValide || hasAttEncaissement || hasEncaisse || some("CHEQUE_ANNULE");
        if (canAnnulerTech && hasEngage) {
            items.push({ type: "divider" });
            items.push({ key: "annulerTechGroup", icon: <CloseCircleOutlined style={{ color: "#a8071a" }} />, label: "Annuler le groupe (technique)" });
        }
        return items;
    };

    const fmtCompagnies = useMemo(() => (compagnies || []).map((c) => ({ value: c.code, label: c.raisonSociale || c.code })), [compagnies]);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#111827" }}><DollarOutlined style={{ marginRight: 8, color: PRIMARY }} />Listing des règlements</Title>
                                    <Text type="secondary">Décaissements de sinistres — {total} règlement(s)</Text>
                                </div>

                                <Space wrap>
                                    {/* Regroupement manuel : ≥ 2 unités cochées (même bénéficiaire). Une unité = un
                                        règlement simple OU un groupe en attente → permet d'ajouter des simples à un
                                        groupe et de fusionner deux groupes. */}
                                    {regroupables.length >= 2 && (
                                        <Button icon={<MergeCellsOutlined />} loading={regrouping} onClick={handleRegrouper}
                                            style={{ borderColor: "#7c3aed", color: "#7c3aed", fontWeight: 600 }}>
                                            {regroupables.some((r) => r.isGroupParent) ? "Regrouper / fusionner" : "Regrouper la sélection"} ({regroupables.length})
                                        </Button>
                                    )}
                                    {/* Export CONTRAINT : BAP → état Bon à payer ; Payé → état des règlements payés */}
                                    <Tooltip title={!statutPaiement ? "Sélectionnez un état de paiement (BAP ou Payé) pour exporter" : ""}>
                                        <Button icon={<FileExcelOutlined />} loading={exporting} onClick={() => handleExport("excel")}
                                            disabled={!statutPaiement || !total} style={!statutPaiement || !total ? undefined : { borderColor: "#059669", color: "#059669" }}>Excel</Button>
                                    </Tooltip>
                                    <Tooltip title={!statutPaiement ? "Sélectionnez un état de paiement (BAP ou Payé) pour exporter" : ""}>
                                        <Button icon={<FilePdfOutlined />} loading={exporting} onClick={() => handleExport("pdf")}
                                            disabled={!statutPaiement || !total} style={!statutPaiement || !total ? undefined : { borderColor: "#dc2626", color: "#dc2626" }}>PDF</Button>
                                    </Tooltip>

                                    {/* Règlement multiple (experts / entités de constat) */}
                                    <Dropdown trigger={["click"]} menu={{ items: reglementMultipleItems, onClick: (e) => onReglementMultipleClick(e.key) }}>
                                        <Button type="primary" icon={<PlusOutlined />} style={{ background: PRIMARY, border: "none", borderRadius: 8, fontWeight: 600 }}>
                                            Règlement multiple <DownOutlined />
                                        </Button>
                                    </Dropdown>
                                </Space>
                            </div>

                            <Spin spinning={loading}>
                                {/* Filtres */}
                                <Card bordered={false} style={{ borderRadius: 12, marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: 12 }}>
                                    <Row gutter={[12, 12]} align="middle">
                                        <Col xs={24} md={isPool ? 6 : 8}>
                                            <RangePicker style={{ width: "100%" }} value={periode} onChange={(d) => { setPeriode(d); setPage(1); }} format="DD/MM/YYYY" placeholder={["Début", "Fin"]} allowClear />
                                        </Col>
                                        {isPool && (
                                            <Col xs={24} md={6}>
                                                <Select showSearch allowClear style={{ width: "100%" }} placeholder="Compagnie…" optionFilterProp="label"
                                                    value={compagnie || undefined} onChange={(v) => { setCompagnie(v || null); setPage(1); }} options={fmtCompagnies} />
                                            </Col>
                                        )}
                                        <Col xs={24} md={isPool ? 6 : 8}>
                                            <Select showSearch allowClear style={{ width: "100%" }} placeholder="Toutes les branches" optionFilterProp="label"
                                                value={branche || undefined} onChange={(v) => { setBranche(v || null); setPage(1); }}
                                                options={branches.map((b) => ({ value: b.codeBranche, label: b.nom ? `${b.nom} (${b.codeBranche})` : b.codeBranche }))} />
                                        </Col>
                                        <Col xs={24} md={isPool ? 6 : 8}>
                                            {/* Statut de paiement : BAP (validé, en attente de chèque) / Payé (avec chèque) */}
                                            <Select allowClear style={{ width: "100%" }} placeholder="Statut de paiement…"
                                                value={statutPaiement || undefined} onChange={(v) => { setStatutPaiement(v || null); setPage(1); }}
                                                options={[
                                                    { value: "BAP", label: "BAP (validé, en attente de chèque)" },
                                                    { value: "PAYE", label: "Payé (avec chèque)" },
                                                ]} />
                                        </Col>
                                    </Row>
                                </Card>

                                <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                                    <style>{GROUPE_CSS}</style>
                                    <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                        <Input allowClear prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                                            placeholder="Rechercher par bénéficiaire, assuré, police, n°…"
                                            value={searchText} onChange={(e) => setSearchText(e.target.value)}
                                            style={{ maxWidth: 380 }} />
                                        {searchText && (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {(loading || searchText.trim() !== searchQuery)
                                                    ? "Recherche sur tout le mois…"
                                                    : `${filteredDataSource.length} résultat(s) sur ${total} règlement(s) du mois`}
                                            </Text>
                                        )}
                                    </div>
                                    <Table
                                        rowKey="codeId"
                                        className="vdv2-table"
                                        rowSelection={rowSelection}
                                        rowClassName={(r) => (r?.isGroupParent ? "vdv2-group-row" : r?.isGroupChild ? "vdv2-group-child-row" : "")}
                                        columns={columns}
                                        dataSource={filteredDataSource}
                                        expandable={{ indentSize: 0, defaultExpandAllRows: true, expandRowByClick: true }}
                                        scroll={{ x: "max-content" }}
                                        locale={{ emptyText: <Empty description="Aucun règlement" /> }}
                                        pagination={searchText ? {
                                            pageSize, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"],
                                            showTotal: (t) => `${t} résultat(s) filtré(s)`,
                                            onShowSizeChange: (_, s) => setPageSize(s),
                                        } : {
                                            current: page, pageSize, total, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"],
                                            showTotal: (t) => `${t} règlement(s)`,
                                            onChange: (p, s) => { setPage(p); setPageSize(s); },
                                        }}
                                    />
                                </Card>
                            </Spin>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            {modalAssignCheque && (
                <AssignCheque
                    open={modalAssignCheque}
                    decaissements={assignChequeGroupe}
                    onCancel={() => { setModalAssignCheque(false); setAssignChequeGroupe(null); }}
                    onSuccess={() => { setModalAssignCheque(false); setAssignChequeGroupe(null); refresh(); }}
                />
            )}
            {modalRemiseCheque && (
                <RemiseCheque
                    open={modalRemiseCheque}
                    decaissement={remiseDecaissement}
                    sinistre={null}
                    onCancel={() => { setModalRemiseCheque(false); setRemiseDecaissement(null); }}
                    onSuccess={() => { setModalRemiseCheque(false); setRemiseDecaissement(null); refresh(); }}
                />
            )}
            {fixCategorieData && (
                <FixEnginCategorieModal
                    open={!!fixCategorieData}
                    enginCodeId={fixCategorieData.enginCodeId}
                    immatriculation={fixCategorieData.immatriculation}
                    onFixed={async () => {
                        const { decaissementCodeId: codeId, dateReglement } = fixCategorieData;
                        setFixCategorieData(null);
                        try { await validateDecaissement(codeId, dateReglement); toast.success("Décaissement validé avec succès"); refresh(); }
                        catch (e) { toast.error(messageRefusAntidatage(e) || "Erreur lors de la validation après correction"); }
                    }}
                    onClose={() => setFixCategorieData(null)}
                />
            )}

            {/* Aperçu INTERACTIF de l'ordre de dépense groupé : saisie du motif (règlements
                multiples) puis impression. */}
            <Modal
                title="Ordre de dépense groupé"
                centered
                open={modalOrdreDepense}
                onCancel={() => setModalOrdreDepense(false)}
                width={900}
                footer={[
                    <Button key="close" onClick={() => setModalOrdreDepense(false)}>Fermer</Button>,
                    <Button key="print" type="primary" icon={<FilePdfOutlined />} style={{ background: "#242166" }}
                        onClick={exportODToPdf}>Imprimer</Button>,
                ]}
            >
                {/* Motif du règlement (apparaît après « Représentant … » sur l'OD) — éditable par HSI089
                    pour un règlement VALIDÉ, ET pour tout règlement GROUPÉ (états mixtes) ; sauf paiement
                    d'un établissement (motif imposé « règlement de factures »). */}
                {canEditMotif && !ordreDepense?.beneficiaireMoral
                    && (ordreDepense?.etatDecaissement === "VALIDE" || ordreDepense?.isReglementMultiple) && (
                    <div style={{ marginBottom: 12, padding: "10px 12px", background: "#f5f7ff", border: "1px solid #d6ddff", borderRadius: 8 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, color: "#242166" }}>
                            Motif du règlement{ordreDepense?.isReglementMultiple ? " (appliqué à tous les règlements du groupe)" : ""}
                        </div>
                        <Input.TextArea rows={2} value={odMotif} onChange={(e) => setOdMotif(e.target.value)}
                            placeholder="Saisir le motif du règlement…" />
                        <div style={{ marginTop: 8, textAlign: "right" }}>
                            <Button type="primary" loading={savingOdMotif} onClick={enregistrerMotifOD} style={{ background: "#242166" }}>
                                Enregistrer le motif
                            </Button>
                        </div>
                    </div>
                )}
                <div style={{ maxHeight: "62vh", overflowY: "auto" }}>
                    <div ref={printODRef}>
                        <OrdreDeDepense decaissement={ordreDepense} sinistre={docSinistre} />
                    </div>
                </div>
            </Modal>

            {/* Hidden printable components for grouped documents */}
            <div style={{ display: "none" }}>
                <div ref={printFDRef}>
                    <FicheDemandeReglementSinistre decaissement={ficheDemandeReglement} sinistre={docSinistre} />
                </div>
                <div ref={printQRef}>
                    <QuittanceIndemnite decaissement={quittance} sinistre={docSinistre} />
                </div>
            </div>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default ValidationDecaissementsV2;
